import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { smartImportApi, EXTENSIONS_IMPORT, ETATS_EN_COURS } from '../lib/smartImportApi';
import { organizationsAPI } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure } from '../lib/formats';
import { PageHeader } from '../components/ui/page-header';
import { Button } from '../components/ui/button';
import { SearchableSelect } from '../components/ui/searchable-select';
import { StatutBadge } from '../components/ui/StatutBadge';
import { LoadingState } from '../components/ui/loading-state';
import TableauBrouillon from '../components/fleetImport/TableauBrouillon';
import RapportImport from '../components/fleetImport/RapportImport';

/**
 * Import intelligent (super administrateur) : photo, fichier Excel ou PDF vers les données d'une société.
 *
 * Société cible choisie ici (jamais déduite) -> dépôt -> lecture et reconnaissance du type en arrière-plan
 * (on peut quitter la page et revenir) -> brouillon à relire, un onglet par type reconnu -> confirmation ->
 * rapport ligne par ligne. Rien n'est enregistré avant la confirmation.
 */

const SCRUTATION_MS = 3000;

const nombreLignes = (job) =>
  Object.values(job?.summary || {}).reduce((somme, bloc) => somme + (bloc?.included || 0), 0);

const SmartImportPage = () => {
  const champ = useRef(null);
  const [societes, setSocietes] = useState([]);
  const [societe, setSociete] = useState('');
  const [job, setJob] = useState(null);
  const [chargement, setChargement] = useState(false);
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    let actif = true;
    organizationsAPI
      .getEvery()
      .then((liste) => actif && setSocietes(liste || []))
      .catch((erreur) => toast.error(messageErreur(erreur, 'Impossible de charger les sociétés')));
    return () => {
      actif = false;
    };
  }, []);

  const charger = useCallback(async () => {
    setJob(null);
    if (!societe) return;
    setChargement(true);
    try {
      setJob(await smartImportApi.current(societe));
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de lire l’import en cours'));
    } finally {
      setChargement(false);
    }
  }, [societe]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Lecture et enregistrement tournent sur le serveur : on relit l'état tant qu'ils durent.
  const jobId = job?.id;
  const statut = job?.status;
  useEffect(() => {
    if (!jobId || !ETATS_EN_COURS.includes(statut)) return undefined;
    const minuteur = setInterval(async () => {
      try {
        setJob(await smartImportApi.get(jobId));
      } catch (erreur) {
        // Une lecture manquée est sans conséquence : la suivante reprend.
      }
    }, SCRUTATION_MS);
    return () => clearInterval(minuteur);
  }, [jobId, statut]);

  const executer = async (action, message) => {
    setOccupe(true);
    try {
      setJob(await action());
    } catch (erreur) {
      toast.error(messageErreur(erreur, message));
    } finally {
      setOccupe(false);
    }
  };

  const deposer = (evenement) => {
    const fichiers = Array.from(evenement.target.files || []);
    evenement.target.value = '';
    if (fichiers.length) executer(() => smartImportApi.upload(fichiers, societe), 'Impossible de lire les fichiers');
  };

  const termine = job && ['done', 'failed', 'cancelled'].includes(job.status);
  const nouvelImport = Boolean(societe) && !chargement && (!job || termine);
  const aImporter = nombreLignes(job);

  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="smart-import-page">
      <PageHeader
        eyebrow="Plateforme"
        title="Import intelligent"
        description="Choisissez la société, puis déposez des photos, des fichiers Excel ou des PDF : le contenu est reconnu (camions, chauffeurs, clients, transporteurs, fournisseurs) et préparé pour vous. Vous relisez le brouillon avant tout enregistrement."
        meta={job ? <StatutBadge domaine="import_flotte_travail" code={job.status} /> : null}
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm" data-testid="smart-import-org">
          <SearchableSelect
            value={societe}
            onValueChange={setSociete}
            options={societes.map((item) => ({ value: item.id, label: item.name }))}
            placeholder="Choisir la société"
            searchPlaceholder="Rechercher une société…"
            emptyMessage="Aucune société."
          />
        </div>
        {nouvelImport && (
          <>
            <input
              ref={champ}
              type="file"
              multiple
              accept={EXTENSIONS_IMPORT}
              className="sr-only"
              onChange={deposer}
              data-testid="smart-import-file"
            />
            <Button type="button" onClick={() => champ.current?.click()} disabled={occupe} data-testid="smart-import-choose">
              {occupe ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />}
              {job ? 'Nouvel import' : 'Déposer des fichiers'}
            </Button>
          </>
        )}
      </div>

      {chargement && <LoadingState shape="cards" />}

      {nouvelImport && job?.status === 'failed' && (
        <div role="alert" className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm" data-testid="smart-import-failed">
          {job.error_message}
        </div>
      )}
      {nouvelImport && job?.status === 'done' && <RapportImport job={job} api={smartImportApi} />}

      {!chargement && job && ETATS_EN_COURS.includes(job.status) && (
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-6" role="status" data-testid="smart-import-progress">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="font-medium">{job.status === 'queued' || job.status === 'analyzing' ? 'Lecture en cours' : 'Import en cours'}</span>
          <span className="text-sm text-muted-foreground">{job.filename}</span>
        </div>
      )}

      {!chargement && job?.status === 'draft' && (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="font-medium">{job.filename}</span>
            <span className="tabular-nums text-muted-foreground">{formatDateHeure(job.created_at)}</span>
            {(job.warnings || []).map((avertissement) => (
              <span key={avertissement} className="rounded-full border border-flame/30 bg-flame/10 px-2.5 py-0.5 text-xs text-flame-ink">
                {avertissement}
              </span>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              disabled={occupe}
              onClick={() => executer(() => smartImportApi.cancel(job.id), 'Impossible d’annuler l’import')}
              data-testid="smart-import-cancel"
            >
              Annuler l’import
            </Button>
          </div>
          <TableauBrouillon job={job} disabled={occupe} onJob={setJob} api={smartImportApi} />
          <div className="sticky bottom-0 flex justify-end border-t border-border bg-background/95 py-3 backdrop-blur">
            <Button
              type="button"
              size="lg"
              disabled={occupe || aImporter === 0}
              onClick={() => executer(() => smartImportApi.confirm(job.id, job.draft_version), 'Impossible de lancer l’import')}
              data-testid="smart-import-confirm"
            >
              {occupe && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {`Importer ${aImporter} ligne${aImporter > 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartImportPage;
